import React, { ReactNode } from "react";
import { _FooterPetitPrinceRoot } from "./FooterPetitPrince.styled";
import Typography from "../../../../Foundation/Typography/Typography";
import FlexBox from "../../../FlexBox/FlexBox";
import Link from "../../../../Core/Link/Link";
import { IconName } from "design-system/src/lib/svgIcon/components";
import { useHighlightedTypographyContext } from "../../../HighlightedTypography/HighlightedTypography.context";
import htmlParse from "html-react-parser";
import FadeIn from "../../../Animations/FadeIn";

export interface IFooterPetitPrinceProps {
  socialNetworkLabel?: string;
  socialLinks?: {
    facebook?: string;
    instagram?: string;
    tiktok?: string;
    pinterest?: string;
  };
  footerText?: string;
}

type SocialKey = keyof NonNullable<IFooterPetitPrinceProps["socialLinks"]>;
const socialIcon: Record<SocialKey, IconName | ((color: string) => ReactNode)> =
  {
    facebook: (color) => (
      <svg
        width="23"
        height="22"
        viewBox="0 0 23 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          color,
        }}
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M2.75231 0C1.23897 0 0 1.23683 0 2.75018V19.2527C0 20.7639 1.23897 22 2.75231 22H11.1045V14.1106H8.46733V10.6759H11.1045V8.95496C11.1045 6.30074 13.0664 4.22087 15.5713 4.22087H18.0016V8.1041H15.8272C15.2571 8.1041 15.0901 8.42895 15.0901 8.8789V10.6759H18.0016V14.1106H15.0901V22H19.2676C20.7809 22 22.0199 20.7639 22.0199 19.2527V2.75018C22.0199 1.23683 20.7809 0 19.2676 0H2.75231Z"
          fill="currentColor"
        />
      </svg>
    ),
    instagram: (color) => (
      <svg
        width="23"
        height="22"
        viewBox="0 0 23 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          color,
        }}
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M15.1057 10.9982C15.1057 8.73802 13.2726 6.90641 11.0096 6.90641C8.74655 6.90641 6.91351 8.73802 6.91351 10.9982C6.91351 13.262 8.74655 15.0915 11.0096 15.0915C13.2726 15.0915 15.1057 13.262 15.1057 10.9982ZM20.2608 4.24534C20.2608 4.58579 19.9843 4.86014 19.6453 4.86014H17.8016C17.4654 4.86014 17.1889 4.58579 17.1889 4.24534V2.40519C17.1889 2.06616 17.4654 1.7911 17.8016 1.7911H19.6453C19.9843 1.7911 20.2608 2.06616 20.2608 2.40519V4.24534ZM18.5784 0H3.44006C1.5395 0 0 1.53878 0 3.4365V7.16228H5.57303C6.77918 5.46286 8.76361 4.34911 11.0096 4.34911C13.2549 4.34911 15.24 5.46286 16.4454 7.16228H22.0185V3.4365C22.0185 1.53878 20.479 0 18.5784 0ZM17.6672 10.9982C17.6672 14.6735 14.6849 17.6509 11.0096 17.6509C7.33215 17.6509 4.35266 14.6735 4.35266 10.9982C4.35266 10.564 4.3946 10.1325 4.47633 9.71886H0V18.5649C0 20.4605 1.5395 22 3.44006 22H18.5784C20.479 22 22.0185 20.4605 22.0185 18.5649V9.71886H17.5421C17.6239 10.1325 17.6672 10.564 17.6672 10.9982Z"
          fill="currentColor"
        />
      </svg>
    ),
    tiktok: (color) => (
      <svg
        width="20"
        height="22"
        viewBox="0 0 20 22"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{
          color,
        }}
      >
        <path
          d="M10.0403 0.0185635C11.24 0 12.4329 0.0110006 13.6244 0C13.6966 1.40326 14.2012 2.83265 15.2284 3.82477C16.2535 4.84163 17.7036 5.3071 19.1144 5.46454V9.15593C17.7923 9.11261 16.4639 8.8376 15.2642 8.26832C14.7417 8.0318 14.2549 7.72722 13.7784 7.41577C13.7722 10.0944 13.7894 12.7696 13.7612 15.4373C13.6897 16.7188 13.2669 17.9942 12.5216 19.0503C11.3225 20.8083 9.24136 21.9544 7.10381 21.9902C5.79267 22.0651 4.48292 21.7076 3.36567 21.0489C1.51413 19.9571 0.211252 17.9585 0.0214918 15.8133C-0.000509386 15.3548 -0.00807231 14.8969 0.0104912 14.4493C0.1755 12.705 1.03836 11.0363 2.37768 9.90122C3.89576 8.57908 6.02231 7.9493 8.01342 8.32194C8.03198 9.67983 7.97767 11.0363 7.97767 12.3942C7.06806 12.1 6.00512 12.1825 5.21033 12.7346C4.63005 13.1106 4.18934 13.6868 3.9597 14.3386C3.76994 14.8034 3.82426 15.3197 3.83526 15.8133C4.05321 17.3177 5.49978 18.5821 7.04399 18.4452C8.06773 18.4342 9.04885 17.8402 9.58238 16.9705C9.75495 16.6659 9.94815 16.3544 9.95846 15.9962C10.0485 14.3565 10.0128 12.7236 10.0238 11.0838C10.0313 7.38827 10.0128 3.70307 10.041 0.019251L10.0403 0.0185635Z"
          fill="currentColor"
        />
      </svg>
    ),
    pinterest: "logo_pinterest",
  };
const SOCIAL_KEYS_ORDER: SocialKey[] = [
  "facebook",
  "instagram",
  "tiktok",
  "pinterest",
];

const FooterPetitPrince = (props: IFooterPetitPrinceProps) => {
  const { socialNetworkLabel, socialLinks, footerText } = props;
  const { highlightedColor: highlightedColorContext } =
    useHighlightedTypographyContext();

  return (
    <_FooterPetitPrinceRoot>
      {socialNetworkLabel && (
        <FadeIn delay={0.1}>
          <Typography
            variant="body"
            color="white"
            className="footer-petit-prince__social-label"
          >
            {socialNetworkLabel}
          </Typography>
        </FadeIn>
      )}
      {socialLinks && (
        <FlexBox gap={16} className="footer-petit-prince__social-links">
          {SOCIAL_KEYS_ORDER.map((key, index) => {
            const value = socialLinks[key];
            if (!value) return null;
            const iconName = socialIcon[key];
            return (
              <FadeIn delay={0.1 + index * 0.1} duration={0.4}>
                <Link
                  key={key}
                  href={value}
                  url={value}
                  to={value}
                  icon={iconName}
                  target="_blank"
                  label=""
                  color={() => highlightedColorContext || "white"}
                />
              </FadeIn>
            );
          })}
        </FlexBox>
      )}
      {footerText && (
        <FadeIn delay={0.1}>
          <Typography
            variant="body2"
            color="white"
            className="footer-petit-prince__footer-text"
          >
            {htmlParse(footerText)}
          </Typography>
        </FadeIn>
      )}
    </_FooterPetitPrinceRoot>
  );
};

export default FooterPetitPrince;
